import type { Menu as BaseMenu } from "@base-ui/react/menu";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";

// Shared styles for Menu, Select, MultiSelect and Popover.

/** Which side of its trigger a floating panel opens on, and which edge of the trigger it lines up with. */
export type FloatingSide = NonNullable<BaseMenu.Positioner.Props["side"]>;
export type FloatingAlign = NonNullable<BaseMenu.Positioner.Props["align"]>;

/** For the Base UI Positioner. */
// Above modal overlays (up to z-[60]), so a panel opened from inside a modal isn't buried behind it.
export const FLOATING_POSITIONER_CLASS = "z-[80] outline-none";

export const FLOATING_PANEL_CLASS = twMerge(
    // Rows stack with a 4px gap.
    "flex flex-col gap-1",
    // At least as wide as its trigger, and no taller than 24rem or the space left on screen, scrolling past that.
    "min-w-(--anchor-width) max-h-[min(24rem,var(--available-height))] overflow-y-auto custom-scrollbar",
    "rounded-2xl border border-stone-200 bg-white p-3 shadow outline-none",
    // Grows out of the trigger when opening and shrinks back into it when closing.
    "origin-(--transform-origin) transition-[opacity,scale] duration-100",
    "data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0",
);

export const FLOATING_ROW_CLASS = twMerge(
    // Leading, icon, label and trailing sit in one line across the full width.
    "flex w-full items-center gap-2 text-left",
    "rounded px-2 py-1",
    // select-none keeps a quick double click from selecting row text. outline-none since the highlight marks focus.
    "cursor-pointer select-none outline-none",
    // data-highlighted is set on both pointer hover and keyboard focus.
    "data-highlighted:bg-stone-100",
    "data-disabled:cursor-not-allowed data-disabled:opacity-50",
);

/** For a panel with a list: only the list scrolls, keeping the scrollbar inside the padding and a search box fixed above it. Use with FLOATING_SCROLLING_LIST_CLASS. */
export const FLOATING_LIST_PANEL_CLASS = "flex flex-col overflow-hidden";
export const FLOATING_SCROLLING_LIST_CLASS = "custom-scrollbar flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto";

/** For groups of rows inside a panel, such as a section, so they keep the row spacing. */
export const FLOATING_ROW_GROUP_CLASS = "flex flex-col gap-1";

/** Background for the chosen row in single-choice lists. */
export const FLOATING_CHOSEN_ROW_CLASS = "data-checked:bg-stone-100 data-selected:bg-stone-100";

export const FLOATING_SEPARATOR_CLASS = "my-0.5 h-px shrink-0 bg-stone-200";

export const FLOATING_SECTION_TITLE_CLASS = "px-2 pt-1 pb-0.5 text-xs font-semibold text-stone-500";

/** Row layout: leading, icon, label with optional description, trailing. */
export const FloatingRowContent = (props: {
    children: ReactNode;
    icon?: ReactNode;
    description?: ReactNode;
    /** Before the icon, such as a checkbox. */
    leading?: ReactNode;
    /** After the label, such as a count, a check mark or a submenu chevron. */
    trailing?: ReactNode;
    destructive?: boolean;
}) => {
    const hasDescription = props.description !== undefined;

    return (
        <>
            {props.leading}
            {props.icon !== undefined && (
                <span
                    className={twMerge(
                        "flex h-4 w-4 shrink-0 items-center justify-center text-stone-500 [&>svg]:h-4 [&>svg]:w-4",
                        // Aligns the icon with the label on two-line rows.
                        hasDescription && "self-start mt-1",
                        props.destructive === true && "text-red-700",
                    )}
                >
                    {props.icon}
                </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col">
                <Text variant="body500" className={twMerge("truncate", props.destructive === true && "text-red-700")}>
                    {props.children}
                </Text>
                {hasDescription && (
                    <Text variant="smallBody400" className="text-stone-500">
                        {props.description}
                    </Text>
                )}
            </span>
            {props.trailing !== undefined && <span className="flex shrink-0 items-center gap-2">{props.trailing}</span>}
        </>
    );
};

export const FloatingRowCount = (props: { count: number }) => (
    <Text variant="smallBody500" className="text-stone-500">
        {props.count}
    </Text>
);

/** A muted line in place of rows when a list has nothing to show, such as a search that matches nothing. */
export const FloatingEmptyText = (props: { children: ReactNode }) => (
    <Text variant="smallBody500" className="px-2 py-1 text-stone-500">
        {props.children}
    </Text>
);
